import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Room } from "./RoomService";
import {
  RecurringEventTeam,
  RecurringEventTeamInput,
} from "./RecurringEventTeamService";
import { Category } from "./CategoryService";

// GraphQL queries
const RECURRING_EVENT_LIST_QUERY = gql`
  query RecurringEventList(
    $filter: RecurringEventFilter
    $params: PaginationAndSorting
  ) {
    recurringEventList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        clientId
        roomId
        name
        description
        recurrencePattern
        daysOfWeek
        startDate
        endDate
        startTime
        endTime
        color
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        room {
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        recurringEventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_GET_QUERY = gql`
  query RecurringEventGet($id: ID) {
    recurringEventGet(id: $id) {
      code
      data {
        id
        venueId
        clientId
        roomId
        name
        description
        recurrencePattern
        daysOfWeek
        startDate
        endDate
        startTime
        endTime
        color
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        room {
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        recurringEventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_GET_MANY_QUERY = gql`
  query RecurringEventGetMany($ids: [ID]) {
    recurringEventGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        clientId
        roomId
        name
        description
        recurrencePattern
        daysOfWeek
        startDate
        endDate
        startTime
        endTime
        color
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        room {
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        recurringEventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const RECURRING_EVENT_CREATE_MUTATION = gql`
  mutation RecurringEventCreate($params: RecurringEventInput) {
    recurringEventCreate(params: $params) {
      code
      data {
        id
        venueId
        clientId
        roomId
        name
        description
        recurrencePattern
        daysOfWeek
        startDate
        endDate
        startTime
        endTime
        color
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        room {
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        recurringEventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_UPDATE_MUTATION = gql`
  mutation RecurringEventUpdate(
    $where: RecurringEventWhereInput
    $params: RecurringEventInput
  ) {
    recurringEventUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        clientId
        roomId
        name
        description
        recurrencePattern
        daysOfWeek
        startDate
        endDate
        startTime
        endTime
        color
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        room {
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        recurringEventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_REMOVE_MUTATION = gql`
  mutation RecurringEventRemove($where: RecurringEventWhereInput) {
    recurringEventRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface RecurringEvent {
  id: string;
  venueId?: string;
  clientId?: string;
  roomId?: string;
  name?: string;
  description?: string;
  recurrencePattern?: string;
  daysOfWeek?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  color?: string;
  categories?: Category[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  imageUrl?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status?: number;
  room?: Room;
  rooms?: Room[];
  recurringEventTeams?: RecurringEventTeam[];
}

export interface RecurringEventFilter {
  id?: string[];
  venueId?: string[];
  clientId?: string[];
  roomId?: string[];
  name?: string;
  recurrencePattern?: string[];
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
  status?: number[];
}

export interface RecurringEventInput {
  venueId?: string;
  clientId?: string | null | undefined;
  roomId?: string | null | undefined;
  name?: string;
  description?: string | null | undefined;
  recurrencePattern?: string;
  daysOfWeek?: string | null | undefined;
  startDate?: string | null | undefined;
  endDate?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  color?: string | null | undefined;
  categories?: string[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status?: number;
  rooms?: string[] | null | undefined;
  recurringEventTeams?: RecurringEventTeamInput[] | null | undefined;
}

export interface RecurringEventWhereInput {
  id: string;
}

/**
 * Recurring Event service that handles all recurring event-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class RecurringEventService {
  /**
   * Get a list of recurring events with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RecurringEventFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(RECURRING_EVENT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single recurring event by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(RECURRING_EVENT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple recurring events by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new recurring event
   */
  static async create(params: RecurringEventInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing recurring event
   */
  static async update(
    id: string,
    params: RecurringEventInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a recurring event
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }
}
